// Change #100739 - set a payment method's Stripe account (keys) from the CRM.
//
// Publishable keys are shown; secret keys are write-only: the API only says
// whether one is stored (and its last 4 characters). Leave a secret blank to
// keep the stored one. Keys are from Stripe Dashboard > Developers > API keys.

import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import AppModal from "../../Common/AppModal";
import { FetchAPI } from "../../../helpers/APIdata/fetchAPI";

// Change #100755 - straight to the right page in the Stripe Dashboard (new tab).
const STRIPE_KEYS_URL = {
  test: "https://dashboard.stripe.com/test/apikeys",
  live: "https://dashboard.stripe.com/apikeys",
};

const KeysHeading = ({ label, mode }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
    <Typography variant="subtitle2">{label}</Typography>
    <Button
      size="small"
      variant="outlined"
      color={mode === "live" ? "success" : "warning"}
      href={STRIPE_KEYS_URL[mode]}
      target="_blank"
      rel="noopener noreferrer"
      endIcon={<OpenInNewIcon fontSize="small" />}
    >
      Open Stripe {mode === "live" ? "LIVE" : "TEST"} keys
    </Button>
  </Box>
);

const empty = {
  ApiFlagTestModeActive: true,
  ApiTestPublicKey: "",
  ApiLivePublicKey: "",
  ApiTestPrivateKey: "",
  ApiLivePrivateKey: "",
};

export default function StripeKeysModal({ open, onClose, paymentMethodId, onSaved }) {
  const [form, setForm] = useState(empty);
  const [stored, setStored] = useState({});
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !paymentMethodId) return;
    setBusy(true);
    FetchAPI(`/paymentmethod/stripe/${paymentMethodId}`, "GET")
      .then((res) => {
        const d = res?.stripe_data || {};
        setName(d.Name || "");
        setStored(d);
        setForm({
          ApiFlagTestModeActive: String(d.ApiFlagTestModeActive ?? "1") === "1",
          ApiTestPublicKey: d.ApiTestPublicKey || "",
          ApiLivePublicKey: d.ApiLivePublicKey || "",
          ApiTestPrivateKey: "",
          ApiLivePrivateKey: "",
        });
      })
      .catch(() => toast.error("Could not load the Stripe settings"))
      .finally(() => setBusy(false));
  }, [open, paymentMethodId]);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: k === "ApiFlagTestModeActive" ? e.target.checked : e.target.value.trim(),
    }));

  const secretHelp = (k) =>
    String(stored[`${k}Set`]) === "1"
      ? `Saved (ends ${stored[`${k}Last4`]}). Leave blank to keep it.`
      : "Not set.";

  const save = async () => {
    setBusy(true);
    try {
      const res = await FetchAPI("/paymentmethod/stripe", "PUT", [
        {
          Id: paymentMethodId,
          ApiFlagTestModeActive: form.ApiFlagTestModeActive ? 1 : 0,
          ApiTestPublicKey: form.ApiTestPublicKey,
          ApiLivePublicKey: form.ApiLivePublicKey,
          ApiTestPrivateKey: form.ApiTestPrivateKey,
          ApiLivePrivateKey: form.ApiLivePrivateKey,
        },
      ]);
      if (Number(res?.status) === 200) {
        toast.success("Stripe keys saved");
        onSaved?.();
        onClose?.();
      } else {
        toast.error(res?.message || "Could not save the keys");
      }
    } catch (e) {
      toast.error("Could not save the keys");
    } finally {
      setBusy(false);
    }
  };

  const testMode = form.ApiFlagTestModeActive;
  const activeReady = testMode
    ? form.ApiTestPublicKey && (form.ApiTestPrivateKey || String(stored.ApiTestPrivateKeySet) === "1")
    : form.ApiLivePublicKey && (form.ApiLivePrivateKey || String(stored.ApiLivePrivateKeySet) === "1");

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={`Stripe account${name ? ` - ${name}` : ""}`}
      headerAccent="indigo"
      size="sm"
      loading={busy}
      onSubmit={save}
      submitLabel="Save keys"
      submitColor="primary"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Copy the keys from your Stripe Dashboard under{" "}
          <Link href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">
            Developers &gt; API keys
          </Link>
          . Secret keys are stored on the server and never shown again.
        </Typography>

        <FormControlLabel
          control={<Switch checked={testMode} onChange={set("ApiFlagTestModeActive")} />}
          label={testMode ? "Test mode - no real money is taken" : "Live mode - real card payments"}
        />
        {!activeReady && (
          <Alert severity="warning">
            The {testMode ? "test" : "live"} keys are not complete - card payments will not work in {testMode ? "test" : "live"} mode.
          </Alert>
        )}

        <KeysHeading label="Test keys" mode="test" />
        <TextField size="small" fullWidth label="Publishable key (pk_test_…)" value={form.ApiTestPublicKey} onChange={set("ApiTestPublicKey")} />
        <TextField
          size="small"
          fullWidth
          type="password"
          autoComplete="new-password"
          label="Secret key (sk_test_…)"
          value={form.ApiTestPrivateKey}
          onChange={set("ApiTestPrivateKey")}
          helperText={secretHelp("ApiTestPrivateKey")}
        />

        <KeysHeading label="Live keys" mode="live" />
        <TextField size="small" fullWidth label="Publishable key (pk_live_…)" value={form.ApiLivePublicKey} onChange={set("ApiLivePublicKey")} />
        <TextField
          size="small"
          fullWidth
          type="password"
          autoComplete="new-password"
          label="Secret key (sk_live_…)"
          value={form.ApiLivePrivateKey}
          onChange={set("ApiLivePrivateKey")}
          helperText={secretHelp("ApiLivePrivateKey")}
        />
      </Stack>
    </AppModal>
  );
}
